import React from "react";
import {
  FaFileDownload,
  FaCar,
  FaMotorcycle,
  FaTruck,
  FaShuttleVan,
  FaTimes,
} from "react-icons/fa";
import { IoColorPalette } from "react-icons/io5";
import { MdCalendarMonth, MdOutlineNumbers } from "react-icons/md";
import { GiSteeringWheel } from "react-icons/gi";
import { BsTagFill } from "react-icons/bs";
import PageModal from "../Components/PageModal";
import { motion } from "framer-motion";

export default function VehicleDetailsModal({
  vehicle,
  onClose,
  handleDownloadDocument,
}) {
  // Return early if no vehicle data
  if (!vehicle) return null;

  // Helper function to get the appropriate vehicle icon
  const getVehicleIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "car":
        return <FaCar className="h-5 w-5" />;
      case "motorcycle":
        return <FaMotorcycle className="h-5 w-5" />;
      case "truck":
        return <FaTruck className="h-5 w-5" />;
      case "van":
      case "suv":
        return <FaShuttleVan className="h-5 w-5" />;
      default:
        return <FaCar className="h-5 w-5" />;
    }
  };

  return (
    <PageModal>
      <motion.div
        className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-lg"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Header */}
        <div className="bg-[#363062] px-4 py-3 flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-white flex items-center">
            {getVehicleIcon(vehicle.type)}
            <span className="ml-2">
              {vehicle.make} {vehicle.model} Details
            </span>
          </h3>
          <button
            type="button"
            className="text-white hover:text-gray-200 focus:outline-none"
            onClick={onClose}
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="space-y-4">
            {/* Vehicle Image/Icon */}
            <div className="flex justify-center mb-4">
              <motion.div
                className="w-20 h-20 rounded-full bg-[#363062] bg-opacity-10 flex items-center justify-center text-[#363062]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {getVehicleIcon(vehicle.type)}
              </motion.div>
            </div>

            {/* Vehicle Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div
                className="flex items-center bg-gray-50 p-3 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <MdOutlineNumbers className="text-[#363062] mr-3 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Plate Number</p>
                  <p className="font-medium">{vehicle.plateNumber}</p>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center bg-gray-50 p-3 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <GiSteeringWheel className="text-[#363062] mr-3 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Make & Model</p>
                  <p className="font-medium">
                    {vehicle.make} {vehicle.model}
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center bg-gray-50 p-3 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <MdCalendarMonth className="text-[#363062] mr-3 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Year</p>
                  <p className="font-medium">{vehicle.year}</p>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center bg-gray-50 p-3 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <IoColorPalette className="text-[#363062] mr-3 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Color</p>
                  <p className="font-medium">{vehicle.color}</p>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center bg-gray-50 p-3 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FaCar className="text-[#363062] mr-3 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="font-medium capitalize">{vehicle.type}</p>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center bg-gray-50 p-3 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <BsTagFill className="text-[#363062] mr-3 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p
                    className={`font-medium ${
                      vehicle.status === "active"
                        ? "text-[#8FCD00]"
                        : "text-red-500"
                    }`}
                  >
                    {vehicle.status === "active" ? "Active" : "Inactive"}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* OR-CR Document */}
            {vehicle.orcrDocument && (
              <motion.div
                className="mt-4 p-4 border border-gray-100 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <p className="text-sm text-gray-500 mb-2">OR-CR Document</p>
                <motion.button
                  onClick={() => handleDownloadDocument(vehicle.orcrDocument)}
                  className="flex items-center text-[#363062] hover:text-[#8FCD00] transition-colors duration-200"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FaFileDownload className="mr-2 h-4 w-4" />
                  <span>View Document</span>
                </motion.button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <motion.button
            type="button"
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#363062] text-base font-medium text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#363062] sm:ml-3 sm:w-auto sm:text-sm"
            onClick={onClose}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Close
          </motion.button>
        </div>
      </motion.div>
    </PageModal>
  );
}
